import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex gap-4 justify-between pr-10 font-normal" }
const _hoisted_2 = { class: "font-semibold invisible lg:visible" }
const _hoisted_3 = {
  key: 0,
  class: "flex gap-2"
}
const _hoisted_4 = { class: "h-[85vh]" }
const _hoisted_5 = {
  key: 0,
  class: "flex items-center justify-center h-full"
}
const _hoisted_6 = {
  key: 1,
  class: "h-full flex flex-col gap-2"
}
const _hoisted_7 = { class: "flex flex-1 gap-2" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 2,
  class: "flex h-full items-center justify-center"
}

import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "shared/components/loading_state/LoadingSpinner.vue";
import Modal from "shared/components/modals/Modal.vue";
import { useHierarchyTags } from "shared/composables/hierarchyTags";
import { useCriticalPathWithLayout, usePlanConfig } from "shared/composables/planner";
import { useUpdateCriticalPath } from "shared/composables/planner";
import { useHasPermission } from "shared/composables/project";
import { useProjectDurationSettings } from "shared/composables/projectDurationSettings";
import {
  useConfirmationModalIsOpen,
  useSaveBeforeLeaveConfirmationModal,
} from "shared/composables/toast";
import { useTrackEvent } from "shared/composables/tracking";
import { CriticalPathEx, CriticalPathToUpdate } from "shared/types/CriticalPath";
import CriticalPathGraph from "shared/views/critical_path/components/CriticalPathGraph.vue";
import CriticalPathTags from "shared/views/critical_path/components/CriticalPathTags.vue";
import { getCriticalPathContextOrNull } from "shared/views/critical_path/criticalPath";
import { createCriticalPathExOrNull } from "shared/views/critical_path/criticalPathNode";


export default /*#__PURE__*/_defineComponent({
  __name: 'CriticalPathModal',
  props: {
    origin: {},
    initialSelectedNodeId: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();

const isAdmin = useHasPermission(["app_admin", "pct_admin"]);
const isPlannerUser = useHasPermission("planner_user");

const editedCriticalPath = ref<CriticalPathEx | null>(null);
const graphContainer = ref<HTMLDivElement | null>(null);
const mountTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const isTagPanelOpen = ref(false);
const selectedNodeIds = ref<string[]>([]);

const trackEvent = useTrackEvent();

const { criticalPath, isLoading: isCriticalPathLoading, layout } = useCriticalPathWithLayout();
const { updateCriticalPath, isLoading: isUpdateCriticalPathLoading } = useUpdateCriticalPath();
const { hierarchyTags, isLoading: areHierarchyTagsLoading } = useHierarchyTags();
const { planConfig, isLoading: isPlanConfigLoading } = usePlanConfig();
const { projectDurationSettings, isProjectDurationSettingsLoading } = useProjectDurationSettings();

const showSaveBeforeLeaveConfirmationModal = useSaveBeforeLeaveConfirmationModal();

const isConfirmationModalOpen = useConfirmationModalIsOpen();

const readonly = computed(() => !isAdmin && !isPlannerUser);

const criticalPathEx = computed(() =>
  createCriticalPathExOrNull(criticalPath.value, planConfig.value, hierarchyTags.value),
);

const criticalPathContext = computed(() =>
  getCriticalPathContextOrNull(
    criticalPathEx.value,
    planConfig.value,
    hierarchyTags.value,
    projectDurationSettings.value,
  ),
);

const isLoading = computed(
  () =>
    isCriticalPathLoading.value ||
    areHierarchyTagsLoading.value ||
    !!mountTimer.value ||
    isPlanConfigLoading.value ||
    isProjectDurationSettingsLoading.value,
);

const editedOrCriticalPath = computed(() => editedCriticalPath.value || criticalPathEx.value);

const handleSaveClick = () => {
  if (!editedCriticalPath.value) {
    return;
  }
  trackEvent("planner_delta_save");
  const criticalPathToUpdate: CriticalPathToUpdate = {
    nodes: editedCriticalPath.value.nodes.map((node) => ({
      _id: node._id,
      tags: node.tags,
    })),
    edges: editedCriticalPath.value.edges,
    tags: editedCriticalPath.value.tags,
  };
  updateCriticalPath(criticalPathToUpdate)
    .then(() => {
      editedCriticalPath.value = null;
    })
    .catch(() => undefined);
};

const handleCriticalPathChange = (newCriticalPathChange: CriticalPathEx) => {
  editedCriticalPath.value = newCriticalPathChange;
};

const handleClose = () => {
  if (isConfirmationModalOpen.value) {
    return;
  }
  if (editedCriticalPath.value) {
    showSaveBeforeLeaveConfirmationModal().then((confirmed) => {
      if (confirmed) {
        emit("close");
      }
    });
  } else {
    emit("close");
  }
};

onMounted(() => {
  // bit delayed until graph is rendered to make it render correctly
  // not sure why this is needed, but might to do something with that it is shown in the modal
  if (mountTimer.value) {
    clearTimeout(mountTimer.value);
  }
  mountTimer.value = setTimeout(() => {
    mountTimer.value = null;
  }, 500);
  trackEvent("planner_delta_view", { origin: props.origin });
});

onUnmounted(() => {
  if (mountTimer.value) {
    clearTimeout(mountTimer.value);
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    open: true,
    onClose: handleClose,
    customCls: "w-full m-5 xxl:w-2/3"
  }, {
    title: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)("analytics.critical_path.title")), 1),
        (!isLoading.value && editedOrCriticalPath.value && !readonly.value && _unref(isAdmin))
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("button", {
                class: _normalizeClass(["py-0.5 px-4 border rounded cursor-pointer border-gray-300", isTagPanelOpen.value ? 'bg-gray-100' : '']),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (isTagPanelOpen.value = !isTagPanelOpen.value))
              }, _toDisplayString(_unref(t)("analytics.critical_path.similar_components")) + " (" + _toDisplayString(editedOrCriticalPath.value.tags.length) + ") ", 3)
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    content: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(LoadingSpinner)
            ]))
          : _createCommentVNode("", true),
        (!isLoading.value && editedOrCriticalPath.value && _unref(planConfig) && criticalPathContext.value && _unref(layout))
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", {
                  ref_key: "graphContainer",
                  ref: graphContainer,
                  class: "flex-1"
                }, [
                  (graphContainer.value)
                    ? (_openBlock(), _createBlock(CriticalPathGraph, {
                        key: 0,
                        class: "h-full border rounded border-gray-400",
                        criticalPath: editedOrCriticalPath.value,
                        context: criticalPathContext.value,
                        hierarchyTags: _unref(hierarchyTags),
                        height: graphContainer.value.clientHeight,
                        planConfig: _unref(planConfig),
                        selectionMode: isTagPanelOpen.value ? 'simple' : 'criticalPath',
                        readonly: readonly.value,
                        layout: _unref(layout),
                        initialSelectedNodeId: _ctx.initialSelectedNodeId,
                        onChange: handleCriticalPathChange,
                        onSelectNodes: _cache[1] || (_cache[1] = ($event: any) => (selectedNodeIds.value = $event))
                      }, null, 8, ["criticalPath", "context", "hierarchyTags", "height", "planConfig", "selectionMode", "readonly", "layout", "initialSelectedNodeId"]))
                    : _createCommentVNode("", true)
                ], 512),
                (isTagPanelOpen.value)
                  ? (_openBlock(), _createBlock(CriticalPathTags, {
                      key: 0,
                      criticalPath: editedOrCriticalPath.value,
                      context: criticalPathContext.value,
                      selectedNodeIds: selectedNodeIds.value,
                      class: "w-[300px] shrink-0",
                      onChange: handleCriticalPathChange,
                      onClose: _cache[2] || (_cache[2] = ($event: any) => (isTagPanelOpen.value = false))
                    }, null, 8, ["criticalPath", "context", "selectedNodeIds"]))
                  : _createCommentVNode("", true)
              ]),
              (!readonly.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    onClick: handleSaveClick,
                    disabled: _unref(isUpdateCriticalPathLoading) || !editedCriticalPath.value,
                    class: "self-end gap-2 inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-white shadow-sm bg-green-400 hover:bg-green-600 disabled:bg-gray-300"
                  }, [
                    _createTextVNode(_toDisplayString(_unref(t)("buttons.save")) + " ", 1),
                    (_unref(isUpdateCriticalPathLoading))
                      ? (_openBlock(), _createBlock(LoadingSpinner, {
                          key: 0,
                          class: "w-4 h-4"
                        }))
                      : _createCommentVNode("", true)
                  ], 8, _hoisted_8))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        (!isLoading.value && (!editedOrCriticalPath.value || !_unref(planConfig)))
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(_unref(t)("analytics.critical_path.no_critical_path")), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }))
}
}

})